import {
  formatDirectionBarsWindow,
  marketCandleDirectionTitleCase,
} from "./marketRegimeDirectionLookbacks";

function prettifyMarginalTableTitle(value: string): string {
  const v = value.trim();
  if (!v.includes("_")) return v;
  return v
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildRegimeDrilldownTableTitles(
  candleTimeframe?: string | null
): Record<string, string> {
  const long = marketCandleDirectionTitleCase("long", candleTimeframe);
  const short = marketCandleDirectionTitleCase("short", candleTimeframe);
  return {
    market_condition: "Market Condition",
    candle_direction: long,
    candle_direction_short: short,
    trade_direction: "Trade Direction",
    session: "Trading Session",
    market_condition_and_direction: `Market Condition + ${long}`,
    candle_direction_and_trade_direction: `${long} + Trade Direction`,
    candle_direction_short_and_trade_direction: `${short} + Trade Direction`,
    market_condition_candle_direction_and_trade_direction: `Market Condition + ${long} + Trade Direction`,
    market_condition_candle_direction_short_and_trade_direction: `Market Condition + ${short} + Trade Direction`,
    session_and_trade_direction: "Trading Session + Trade Direction",
    session_and_candle_direction: `Trading Session + ${long}`,
    session_and_candle_direction_short: `Trading Session + ${short}`,
    session_and_market_condition: "Trading Session + Market Condition",
    session_and_market_condition_and_trade_direction:
      "Trading Session + Market Condition + Trade Direction",
    session_and_market_condition_and_direction_and_trade_direction: `Trading Session + Market Condition + ${long} + Trade Direction`,
    session_and_market_condition_and_direction_short_and_trade_direction: `Trading Session + Market Condition + ${short} + Trade Direction`,
  };
}

/** Descriptions for Entry Analysis slice-drilldown categories (category column tooltips). */
const REGIME_DRILLDOWN_TABLE_DESCRIPTIONS: Record<string, string> = {
  market_condition:
    "Shows how results change across market regimes at entry, such as trend, range, or expansion.",
  market_condition_and_direction:
    "Shows how results change when you combine market regime with market direction at entry.",
  candle_direction:
    "Shows how results differ by longer-horizon market direction at entry (up, down, or flat).",
  trade_direction: "Shows how results differ by trade side at entry (long versus short).",
  session:
    "Shows how performance changes by trading session at entry (for example Sydney/Tokyo, London, or New York).",
  candle_direction_and_trade_direction:
    "Shows how longer-horizon market direction and trade side interact at entry.",
  candle_direction_short:
    "Shows how results differ by shorter-horizon market direction at entry.",
  candle_direction_short_and_trade_direction:
    "Shows how shorter-horizon market direction and trade side interact at entry.",
  market_condition_candle_direction_and_trade_direction:
    "Shows performance for each combined context of regime, longer-horizon market direction, and trade side at entry.",
  market_condition_candle_direction_short_and_trade_direction:
    "Shows performance for each combined context of regime, shorter-horizon market direction, and trade side at entry.",
  session_and_trade_direction: "Shows how trade side performs within each trading session.",
  session_and_candle_direction:
    "Shows how longer-horizon market direction behaves within each trading session.",
  session_and_candle_direction_short:
    "Shows how shorter-horizon market direction behaves within each trading session.",
  session_and_market_condition:
    "Shows which market regimes appear in each session and how they perform.",
  session_and_market_condition_and_trade_direction:
    "Shows performance by the combined context of session, regime, and trade side.",
  session_and_market_condition_and_direction_and_trade_direction:
    "Shows performance by the full combined context of session, regime, longer-horizon market direction, and trade side.",
  session_and_market_condition_and_direction_short_and_trade_direction:
    "Shows performance by the full combined context of session, regime, shorter-horizon market direction, and trade side.",
};

/** Explorer `breakdown_map` branches → `regime_summary` table keys (naming differs on BE). */
const BREAKDOWN_MAP_TO_TABLE_KEY: Record<string, string> = {
  by_market_condition: "market_condition",
  by_market_direction: "candle_direction",
  by_market_direction_short: "candle_direction_short",
  by_candle_direction: "candle_direction",
  by_candle_direction_short: "candle_direction_short",
  by_trade_direction: "trade_direction",
  by_market_condition_and_direction: "market_condition_and_direction",
  by_market_condition_and_direction_and_trade_direction:
    "market_condition_candle_direction_and_trade_direction",
  by_market_condition_and_direction_short_and_trade_direction:
    "market_condition_candle_direction_short_and_trade_direction",
  by_candle_direction_and_trade_direction: "candle_direction_and_trade_direction",
  by_candle_direction_short_and_trade_direction: "candle_direction_short_and_trade_direction",
  by_session: "session",
  by_session_and_trade_direction: "session_and_trade_direction",
  by_session_and_candle_direction: "session_and_candle_direction",
  by_session_and_candle_direction_short: "session_and_candle_direction_short",
  by_session_and_market_condition: "session_and_market_condition",
  by_session_and_market_condition_and_trade_direction:
    "session_and_market_condition_and_trade_direction",
  by_session_and_market_condition_and_direction_and_trade_direction:
    "session_and_market_condition_and_direction_and_trade_direction",
  by_session_and_market_condition_and_direction_short_and_trade_direction:
    "session_and_market_condition_and_direction_short_and_trade_direction",
};

export function regimeDrilldownTableKeyFromBreakdownMap(breakdownMap: string): string {
  const raw = String(breakdownMap ?? "").trim();
  if (!raw) return "";
  if (BREAKDOWN_MAP_TO_TABLE_KEY[raw]) return BREAKDOWN_MAP_TO_TABLE_KEY[raw]!;
  if (raw.startsWith("by_")) return raw.slice(3);
  return raw;
}

function resolveRegimeDrilldownTableKey(tableKeyOrBreakdownMap: string): string {
  const raw = String(tableKeyOrBreakdownMap ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("by_")) return regimeDrilldownTableKeyFromBreakdownMap(raw);
  return raw;
}

/** Human title for a Regime slice-drilldown table (from table key or explorer breakdown_map). */
export function regimeDrilldownTableTitle(
  tableKeyOrBreakdownMap: string,
  candleTimeframe?: string | null
): string {
  const tableKey = resolveRegimeDrilldownTableKey(tableKeyOrBreakdownMap);
  if (!tableKey) return "—";
  const titles = buildRegimeDrilldownTableTitles(candleTimeframe);
  return titles[tableKey] ?? prettifyMarginalTableTitle(tableKey);
}

/** Accordion description for a Regime slice-drilldown table. */
export function regimeDrilldownTableDescription(
  tableKeyOrBreakdownMap: string,
  candleTimeframe?: string | null
): string {
  const tableKey = resolveRegimeDrilldownTableKey(tableKeyOrBreakdownMap);
  const title = regimeDrilldownTableTitle(tableKey, candleTimeframe);
  return (
    REGIME_DRILLDOWN_TABLE_DESCRIPTIONS[tableKey] ??
    `Groups trades by ${title} at entry to show how performance changes across that context.`
  );
}

export function isCombinedRegimeDrilldownTable(tableKeyOrBreakdownMap: string): boolean {
  const tableKey = resolveRegimeDrilldownTableKey(tableKeyOrBreakdownMap);
  return tableKey.includes("_and_") || tableKey.includes("_x_") || tableKey.includes("_X_");
}

/** Sort order for regime slice-drilldown categories (matches buildRegimeMarginalTableSpecs). */
const REGIME_DRILLDOWN_TABLE_ORDER: string[] = [
  "market_condition",
  "candle_direction",
  "candle_direction_short",
  "trade_direction",
  "session",
  "market_condition_and_direction",
  "candle_direction_and_trade_direction",
  "candle_direction_short_and_trade_direction",
  "market_condition_candle_direction_and_trade_direction",
  "market_condition_candle_direction_short_and_trade_direction",
  "session_and_trade_direction",
  "session_and_candle_direction",
  "session_and_candle_direction_short",
  "session_and_market_condition",
  "session_and_market_condition_and_trade_direction",
  "session_and_market_condition_and_direction_and_trade_direction",
  "session_and_market_condition_and_direction_short_and_trade_direction",
];

export function compareRegimeDrilldownTableKeys(a: string, b: string): number {
  const ak = resolveRegimeDrilldownTableKey(a);
  const bk = resolveRegimeDrilldownTableKey(b);
  const ia = REGIME_DRILLDOWN_TABLE_ORDER.indexOf(ak);
  const ib = REGIME_DRILLDOWN_TABLE_ORDER.indexOf(bk);
  const ra = ia >= 0 ? ia : REGIME_DRILLDOWN_TABLE_ORDER.length;
  const rb = ib >= 0 ? ib : REGIME_DRILLDOWN_TABLE_ORDER.length;
  if (ra !== rb) return ra - rb;
  return ak.localeCompare(bk);
}

/** @deprecated Prefer passing candleTimeframe into regimeDrilldownTableTitle. */
export function formatDirectionBarsForDocs(candleTimeframe?: string | null): {
  short: string;
  long: string;
} {
  return {
    short: formatDirectionBarsWindow("short", candleTimeframe),
    long: formatDirectionBarsWindow("long", candleTimeframe),
  };
}
